import { Outlet, BrowserRouter, Routes, Route, Navigate } from "react-router";

import { AuthContextProvider, useAuth } from '../features/auth/AuthContext.tsx';
import { WebSocketProvider } from './providers/WebSocketContext.tsx';

import AppLayout from './layout/AppLayout.tsx';
import LoginPage from '../features/auth/pages/LoginPage.tsx';
import SignUpPage from '../features/auth/pages/SignUpPage.tsx';
import ItemEditPage from '../features/items/pages/ItemEditPage.tsx';
import ItemListPage from '../features/items/pages/ItemListPage.tsx';
import ItemImportPage from '../features/items/pages/ItemImportPage.tsx';
import { canAccessItemImport, canAccessItemTransfer } from '../features/items/itemImportAccess.ts';
import ShopEditPage from '../features/shops/pages/ShopEditPage.tsx';
import UserSettingsPage from '../features/user-settings/pages/UserSettingsPage.tsx';
import ItemGroupListPage from '../features/item-groups/pages/ItemGroupListPage.tsx';
import ItemGroupEditPage from '../features/item-groups/pages/ItemGroupEditPage.tsx';
import ProcessRecordListPage from '../features/process-records/pages/ProcessRecordListPage.tsx';
import ImpersonatePage from '../features/auth/pages/ImpersonatePage.tsx';

import { AppToastContainer } from '../shared/ui/AppToast';
import { beginImpersonationSession } from '../shared/api/authStorage';
import '../index.css';
import ShopListPage from "../features/shops/pages/ShopListPage.tsx";


declare global {
  interface Window {
    REACT_APP_CONFIG?: Record<string, any>;
  }
}

const consoleAppConfig = window.REACT_APP_CONFIG ?? {};

if (/^\/impersonate\/?$/.test(window.location.pathname)) {
  beginImpersonationSession();
}

const ProtectedRoute = () => {
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated ?? false;
  return isAuthenticated ? (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ) : (
    <Navigate to="/login" />
  );
};

const ItemImportGuard = () => {
  const auth = useAuth();
  return canAccessItemImport(auth) ? <ItemImportPage /> : <Navigate to="/items/" replace />;
};

const ItemTransferGuard = () => {
  const auth = useAuth();
  if (!auth.isSubscriptionStatusLoaded && !auth.isImpersonating()) return null;
  return canAccessItemTransfer(auth) ? <Navigate to="/userconfig/?tab=item_transfer" replace /> : <Navigate to="/items/" replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* 로그인 상태에서만 Layout을 최상위로 배치 (사실상 Logged in Layout 임) */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<ItemListPage />} />
        <Route path="/items/" element={<ItemListPage />} />
        <Route path="/items/import/" element={<ItemImportGuard />} />
        <Route path="/items/transfer/" element={<ItemTransferGuard />} />
        <Route path="/items/new/" element={<ItemEditPage />} />
        <Route path="/items/:id/" element={<ItemEditPage />} />
        <Route path="/itemgroups/" element={<ItemGroupListPage />} />
        <Route path="/itemgroups/new/" element={<ItemGroupEditPage />} />
        <Route path="/itemgroups/:id/" element={<ItemGroupEditPage />} />
        <Route path="/records/" element={<ProcessRecordListPage />} />
        <Route path="/shops/" element={<ShopListPage />} />
        <Route path="/shops/new/" element={<ShopEditPage />} />
        <Route path="/shops/:id/" element={<ShopEditPage />} />
        <Route path="/userconfig/" element={<UserSettingsPage />} />
      </Route>
      {/* 로그인/회원가입 페이지는 Layout 밖 */}
      <Route path="/login/" element={<LoginPage />} />
      <Route path="/signup/" element={<SignUpPage />} />
      <Route path="/impersonate/" element={<ImpersonatePage />} />
    </Routes>
  );
};

const App = () => {
  if (consoleAppConfig.isTest) {
    return <div>Test Mode</div>;
  }
  return (
    <BrowserRouter>
      <AuthContextProvider>
        <WebSocketProvider>
          <AppRoutes />
          <AppToastContainer />
        </WebSocketProvider>
      </AuthContextProvider>
    </BrowserRouter>
  );
};

export default App;
